// "Matching assist mode" (gamemaster-enabled): pick a reference location (GPS,
// a dragged pin, or a typed address), then tap target chips to see the closest
// fair-game features with "as-the-crow-flies" distances + colored lines.
//
// Discrete POIs show the 3 closest; continuous targets (coastline, division
// borders) show the single nearest point; "elevation" is a sea-level readout.
import * as turf from "@turf/turf";
import { GameMap, AIRPORT_POINTS } from "./map";
import { GameClient } from "./net";
import { STATIONS } from "./stations";
import { POIS, POI_CATEGORIES, parkFC } from "./pois";
import { DIVISIONS } from "./divisions";
import { geocodeArea } from "./api";
import { esc } from "./ui/util";
import coastlineData from "../public/data/coastline.json";

type LL = [number, number];
type Kind = "points" | "parks" | "coastline" | "division" | "elevation";
interface AssistTarget { id: string; label: string; emoji: string; color: string; kind: Kind; divId?: string; pts?: { name: string; lat: number; lng: number }[]; }
interface NearResult { name: string; dist: number; point: LL; }

const EXTRA: AssistTarget[] = [
  { id: "airport", label: "Airports", emoji: "✈️", color: "#2563eb", kind: "points", pts: AIRPORT_POINTS },
  { id: "coastline", label: "Coastline / water", emoji: "🌊", color: "#0ea5e9", kind: "coastline" },
  { id: "border-communityboard", label: "Community-board border", emoji: "🗳️", color: "#db2777", kind: "division", divId: "communityboard" },
  { id: "border-congressional", label: "Congressional border", emoji: "🏛️", color: "#9333ea", kind: "division", divId: "congressional" },
  { id: "border-neighborhood", label: "Neighborhood border", emoji: "🏘️", color: "#0d9488", kind: "division", divId: "neighborhood" },
  { id: "elevation", label: "Elevation above sea level", emoji: "⛰️", color: "#64748b", kind: "elevation" },
];
export const ASSIST_TARGETS: AssistTarget[] = [
  ...POI_CATEGORIES.map((c) => ({ id: c.id, label: c.label, emoji: c.emoji, color: c.color, kind: (c.polygon ? "parks" : "points") as Kind })),
  ...EXTRA,
];

const coastline = coastlineData as unknown as GeoJSON.FeatureCollection;
function distMi(a: LL, b: LL): number {
  const R = 3958.8, toR = Math.PI / 180;
  const dLat = (b[1] - a[1]) * toR, dLng = (b[0] - a[0]) * toR;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * toR) * Math.cos(b[1] * toR) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}
function fmt(mi: number): string { return mi < 0.5 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(2)} mi`; }
function centroidOf(g: GeoJSON.Geometry): LL {
  if (g.type === "Point") return g.coordinates as LL;
  const pts: number[][] = [];
  const walk = (c: unknown): void => { if (typeof (c as number[])[0] === "number") pts.push(c as number[]); else (c as unknown[]).forEach(walk); };
  walk((g as { coordinates: unknown }).coordinates);
  const n = pts.length || 1;
  return [pts.reduce((s, p) => s + p[0], 0) / n, pts.reduce((s, p) => s + p[1], 0) / n];
}

// ---- nearest-feature computation (cached geometry derivations) -------------
let parkCache: GeoJSON.Feature[] | null = null;
function parkPolys(): GeoJSON.Feature[] { return (parkCache ??= parkFC().features); }
const divLineCache: Record<string, GeoJSON.Feature[]> = {};
function divisionLines(divId: string): GeoJSON.Feature[] {
  if (divLineCache[divId]) return divLineCache[divId];
  const d = DIVISIONS.find((x) => x.id === divId);
  const out: GeoJSON.Feature[] = [];
  if (d) for (const f of d.data.features) {
    try {
      const l = turf.polygonToLine(f as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>);
      if (l.type === "FeatureCollection") out.push(...(l.features as GeoJSON.Feature[]));
      else out.push(l as GeoJSON.Feature);
    } catch { /* skip */ }
  }
  return (divLineCache[divId] = out);
}
function nearestOnLines(ref: LL, features: GeoJSON.Feature[]): NearResult | null {
  const refPt = turf.point(ref);
  let best: NearResult | null = null;
  for (const f of features) {
    if (f.geometry?.type !== "LineString") continue;
    try {
      const np = turf.nearestPointOnLine(f as GeoJSON.Feature<GeoJSON.LineString>, refPt, { units: "miles" });
      const d = np.properties.dist as number;
      if (!best || d < best.dist) best = { name: "", point: np.geometry.coordinates as LL, dist: d };
    } catch { /* skip */ }
  }
  return best;
}
function nearestParks(ref: LL): NearResult[] {
  const refPt = turf.point(ref);
  const cand = parkPolys()
    .map((f) => ({ f, cd: distMi(ref, turf.centroid(f).geometry.coordinates as LL) }))
    .filter((x) => x.cd < 12).sort((a, b) => a.cd - b.cd).slice(0, 60);
  const res: NearResult[] = [];
  for (const { f } of cand) {
    const l = turf.polygonToLine(f as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>);
    const lines = l.type === "FeatureCollection" ? (l.features as GeoJSON.Feature[]) : [l as GeoJSON.Feature];
    let best: { point: LL; dist: number } | null = null;
    for (const ln of lines) {
      if (ln.geometry?.type !== "LineString") continue;
      try { const np = turf.nearestPointOnLine(ln as GeoJSON.Feature<GeoJSON.LineString>, refPt, { units: "miles" }); const d = np.properties.dist as number; if (!best || d < best.dist) best = { point: np.geometry.coordinates as LL, dist: d }; } catch { /* skip */ }
    }
    if (best) res.push({ name: (f.properties?.name as string) || "Park", point: best.point, dist: best.dist });
  }
  return res.sort((a, b) => a.dist - b.dist).slice(0, 3);
}
function computeTarget(t: AssistTarget, ref: LL): NearResult[] {
  if (t.kind === "points") {
    return (t.pts || POIS[t.id] || []).map((p) => ({ name: p.name || t.label, point: [p.lng, p.lat] as LL, dist: distMi(ref, [p.lng, p.lat]) }))
      .sort((a, b) => a.dist - b.dist).slice(0, 3);
  }
  if (t.kind === "parks") return nearestParks(ref);
  if (t.kind === "coastline") { const r = nearestOnLines(ref, coastline.features); return r ? [{ ...r, name: "Water's edge" }] : []; }
  if (t.kind === "division") { const r = nearestOnLines(ref, divisionLines(t.divId!)); return r ? [{ ...r, name: "Nearest border" }] : []; }
  return [];
}
async function fetchElevationFt(ref: LL): Promise<number | null> {
  try {
    const r = await fetch(`https://api.open-meteo.com/v1/elevation?latitude=${ref[1].toFixed(5)}&longitude=${ref[0].toFixed(5)}`);
    const j = await r.json();
    const m = j?.elevation?.[0];
    return typeof m === "number" ? m * 3.28084 : null;
  } catch { return null; }
}

// Kinds with a discrete "closest feature" identity, so "is your closest X the
// same as mine?" can drive an elimination (POI points, airports, parks).
const MATCHABLE: Kind[] = ["points", "parks"];
// A stable id for the closest feature of target `t` to point `ll` (its location).
function closestFeatureKey(t: AssistTarget, ll: LL): string | null {
  const res = computeTarget(t, ll);
  if (!res.length) return null;
  const p = res[0].point;
  return `${p[0].toFixed(5)},${p[1].toFixed(5)}`;
}
// Stations ruled out by the hider's "same"/"different" answer about their
// closest feature in category `t`. "yes" (same as the seeker's M0) keeps only
// M0's cell; "no" removes it.
function matchingEliminate(t: AssistTarget, ref: LL, answer: "yes" | "no", client: GameClient): string[] {
  const m0 = closestFeatureKey(t, ref);
  if (!m0) return [];
  const out: string[] = [];
  for (const s of STATIONS) {
    if (client.statusOf(s.id) !== "active") continue;
    const same = closestFeatureKey(t, [s.lng, s.lat]) === m0;
    if (answer === "yes" ? !same : same) out.push(s.id);
  }
  return out;
}

// ---- panel ----------------------------------------------------------------
export function mountAssist(root: HTMLElement, client: GameClient, gameMap: GameMap) {
  let ref: LL | null = null;
  let refMode: "gps" | "pin" | "address" | null = null;
  let gpsWatch: number | null = null;
  let elevationFt: number | null = null;
  let elevKey = "";
  const active = new Set<string>();
  const computed: Record<string, NearResult[]> = {};

  function stopGps() { if (gpsWatch != null) { navigator.geolocation.clearWatch(gpsWatch); gpsWatch = null; } }
  function setRef(ll: LL, mode: "pin" | "address") {
    stopGps();
    ref = ll; refMode = mode;
    gameMap.setReferenceMarker(ll, mode === "pin", (p) => { ref = p; recompute(); });
    recompute(); render();
  }
  function startGps() {
    if (!navigator.geolocation) { window.alert("Geolocation isn't available on this device."); return; }
    refMode = "gps"; render();
    let firstFix = true;
    gpsWatch = navigator.geolocation.watchPosition(
      (pos) => {
        const next: LL = [pos.coords.longitude, pos.coords.latitude];
        if (ref && distMi(ref, next) < 0.004) return; // ignore jitter (<~20ft)
        ref = next; gameMap.setReferenceMarker(next, false); recompute(); render();
        if (firstFix) { firstFix = false; gameMap.flyAboveSheet(next, 15); } // centre above the card
      },
      () => { window.alert("Couldn't read your location — check location permission."); stopGps(); refMode = null; render(); },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 });
  }
  function dropPin() { const c = gameMap.map.getCenter(); gameMap.flyAboveSheet([c.lng, c.lat], 15); setRef([c.lng, c.lat], "pin"); }
  async function enterAddress() {
    const q = window.prompt("Address or street corner (e.g. “5th Ave & 42nd St, NY”):");
    if (!q || !q.trim()) return;
    try { const { geometry } = await geocodeArea(q.trim()); const pt = centroidOf(geometry); gameMap.flyAboveSheet(pt, 15); setRef(pt, "address"); }
    catch { window.alert("Couldn't find that location."); }
  }

  function recompute() {
    if (!ref) { gameMap.clearAssistOverlay(); gameMap.clearInfoLabel(); return; }
    const feats: GeoJSON.Feature[] = [];
    for (const id of active) {
      const t = ASSIST_TARGETS.find((x) => x.id === id)!;
      if (t.kind === "elevation") continue;
      const res = computeTarget(t, ref);
      computed[id] = res;
      res.forEach((r, i) => {
        feats.push({ type: "Feature", geometry: { type: "LineString", coordinates: [ref!, r.point] }, properties: { color: t.color } });
        feats.push({ type: "Feature", geometry: { type: "Point", coordinates: r.point }, properties: { color: t.color, label: fmt(r.dist), closest: i === 0 } });
      });
    }
    gameMap.setAssistOverlay(feats);
    // A compact summary pinned by the centre: the single CLOSEST feature of each
    // active category, so you don't have to scan the list (e.g. "✈️ LGA · 2.3 mi").
    const summary: string[] = [];
    for (const id of active) {
      const t = ASSIST_TARGETS.find((x) => x.id === id)!;
      if (t.kind === "elevation") continue;
      const r0 = computed[id]?.[0];
      if (r0) summary.push(`<span class="al-dot" style="background:${t.color}"></span>${esc(r0.name)} · <b>${fmt(r0.dist)}</b>`);
    }
    if (summary.length) gameMap.setInfoLabel(ref, `<div class="assist-closest">${summary.join("")}</div>`);
    else gameMap.clearInfoLabel();
    if (active.has("elevation")) {
      const key = `${ref[0].toFixed(4)},${ref[1].toFixed(4)}`;
      if (key !== elevKey) { elevKey = key; elevationFt = null; fetchElevationFt(ref).then((ft) => { elevationFt = ft; renderResults(); }); }
    }
    renderResults();
  }

  function panelHtml(): string {
    if (!client.config.matchingAssist) return "";
    const status = !ref ? "Pick your location to begin." : refMode === "gps" ? "📡 Live GPS" : refMode === "pin" ? "📍 Drag the pin to move" : "🏠 Address set";
    const chips = ASSIST_TARGETS.map((t) => `<button class="chip ${active.has(t.id) ? "on" : ""}" data-t="${t.id}" style="--c:${t.color}">${t.emoji} ${t.label}</button>`).join("");
    return `<details class="block assist-block" open><summary>📍 How close am I?</summary>
      <div class="assist">
        <div class="ar-row">
          <button class="ar-btn ${refMode === "gps" ? "on" : ""}" id="ar-gps">📡 GPS</button>
          <button class="ar-btn ${refMode === "pin" ? "on" : ""}" id="ar-pin">📍 Pin</button>
          <button class="ar-btn ${refMode === "address" ? "on" : ""}" id="ar-addr">🏠 Address</button>
        </div>
        <div class="ar-status">${status}</div>
        <div class="assist-chips ${ref ? "" : "dim"}">${chips}</div>
        <div class="assist-results" id="assist-results"></div>
      </div></details>`;
  }
  function renderResults() {
    const el = root.querySelector<HTMLElement>("#assist-results");
    if (!el) return;
    if (!ref) { el.innerHTML = ""; return; }
    if (!active.size) { el.innerHTML = `<div class="muted">Tap a target above to measure.</div>`; return; }
    el.innerHTML = [...active].map((id) => {
      const t = ASSIST_TARGETS.find((x) => x.id === id)!;
      if (t.kind === "elevation") return `<div class="arr" style="--c:${t.color}"><div class="arr-h">${t.emoji} ${t.label}</div><div class="arr-elev">${elevationFt == null ? "measuring…" : `<b>${Math.round(elevationFt).toLocaleString()} ft</b> above sea level`}</div></div>`;
      const res = computed[id] || [];
      const items = res.map((r, i) => `<li class="${i === 0 ? "closest" : ""}"><span class="arr-n">${esc(r.name)}</span><span class="arr-d">${fmt(r.dist)}</span></li>`).join("") || `<li class="muted">none nearby</li>`;
      // Matching elimination: ask the hider "is your closest X the same as mine?"
      const match = MATCHABLE.includes(t.kind) && res.length
        ? `<div class="arr-match"><div class="arr-matchq">Hider's closest ${esc(t.label.toLowerCase())} vs mine?</div>
            <div class="arr-matchbtns"><button class="arr-mbtn same" data-match="${esc(id)}:yes">Same → cut the rest</button><button class="arr-mbtn diff" data-match="${esc(id)}:no">Different → cut this one</button></div></div>`
        : "";
      return `<div class="arr" style="--c:${t.color}"><div class="arr-h">${t.emoji} ${t.label}</div><ol class="arr-list">${items}</ol>${match}</div>`;
    }).join("");
    el.querySelectorAll<HTMLButtonElement>("[data-match]").forEach((b) => b.addEventListener("click", () => {
      if (!ref) return;
      const [id, ans] = b.dataset.match!.split(":");
      const t = ASSIST_TARGETS.find((x) => x.id === id)!;
      const answer = ans as "yes" | "no";
      const ids = matchingEliminate(t, ref, answer, client);
      if (!ids.length) { window.alert("Nothing new to eliminate from that answer."); return; }
      if (!window.confirm(`Eliminate ${ids.length} station${ids.length === 1 ? "" : "s"} where the closest ${t.label.toLowerCase()} is ${answer === "yes" ? "different from" : "the same as"} yours?`)) return;
      client.eliminate(ids, `Matching: closest ${t.label.toLowerCase()} ${answer === "yes" ? "same" : "different"}`);
    }));
  }
  function render() { root.innerHTML = panelHtml(); wire(); renderResults(); }
  function wire() {
    root.querySelector("#ar-gps")?.addEventListener("click", startGps);
    root.querySelector("#ar-pin")?.addEventListener("click", dropPin);
    root.querySelector("#ar-addr")?.addEventListener("click", enterAddress);
    root.querySelectorAll<HTMLButtonElement>("[data-t]").forEach((b) => b.addEventListener("click", () => {
      if (!ref) { window.alert("Set your location first — GPS, Pin, or Address."); return; }
      const id = b.dataset.t!;
      if (active.has(id)) { active.delete(id); delete computed[id]; } else active.add(id);
      recompute(); render();
    }));
  }

  client.subscribe(() => {
    if (!client.config.matchingAssist && (ref || active.size || gpsWatch != null)) {
      stopGps(); active.clear(); ref = null; refMode = null;
      gameMap.clearAssistOverlay(); gameMap.clearReferenceMarker();
    }
    render();
  });
}
